import React, { useEffect, useState } from 'react';
import { ChatCircleDots, PencilSimple, Trash, PaperPlaneTilt, Sparkle } from '@phosphor-icons/react';
import api from '@/api';
import { toast } from 'sonner';
import { timeAgo, formatDateTime } from '@/utils/datetime';
export default function ApplicationComments({ programId, applicationId }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState('');

  const base = `/vc/programs/${programId}/applications/${applicationId}/comments`;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(base);
        // AI-authored notes are shown in the dedicated AI Evaluation panel above,
        // not duplicated into this internal discussion thread.
        if (!cancelled) setComments(res.data.filter(c => !c.is_ai));
      } catch {
        if (!cancelled) toast.error('Failed to load comments');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [base]);

  const post = async () => {
    const body = draft.trim();
    if (!body || posting) return;
    setPosting(true);
    try {
      const res = await api.post(base, { body });
      setComments(c => [...c, res.data]);
      setDraft('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not post comment'); }
    finally { setPosting(false); }
  };

  const saveEdit = async (id) => {
    const body = editDraft.trim();
    if (!body) return;
    try {
      const res = await api.patch(`${base}/${id}`, { body });
      setComments(c => c.map(x => (x.id === id ? res.data : x)));
      setEditingId(null);
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not save comment'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      await api.delete(`${base}/${id}`);
      setComments(c => c.filter(x => x.id !== id));
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not delete comment'); }
  };

  return (
    <div data-testid="app-comments">
      <div className="text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-2 flex items-center gap-1.5">
        <ChatCircleDots size={12} weight="bold" /> Comments
        {comments.length > 0 && <span className="font-mono text-text-secondary">{comments.length}</span>}
        <span className="normal-case tracking-normal text-text-muted font-normal">· internal, never shown to the applicant</span>
      </div>

      {loading ? (
        <div className="text-xs text-text-muted italic">Loading comments…</div>
      ) : (
        <div className="space-y-2 mb-3">
          {comments.length === 0 && (
            <div className="text-xs text-text-muted italic bg-surface border border-border rounded-lg p-3" data-testid="no-comments">
              No comments yet. Record your read on this applicant so the rest of the team sees it.
            </div>
          )}
          {comments.map(c => (
            <div key={c.id} className={`bg-surface border rounded-lg ${c.is_ai ? 'border-primary/30' : 'border-border'}`} data-testid={`comment-${c.id}`}>
              <div className={`flex items-center gap-2 px-3 py-2 border-b ${c.is_ai ? 'border-primary/20 bg-primary/5' : 'border-border'}`}>
                {c.is_ai ? (
                  <div className="w-5 h-5 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0">
                    <Sparkle size={11} weight="fill" className="text-primary" />
                  </div>
                ) : (
                  <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-primary font-mono text-[9px] font-bold">{(c.author_name || '?').substring(0, 2).toUpperCase()}</span>
                  </div>
                )}
                <span className="text-[11px] font-semibold text-text-primary truncate">{c.author_name}</span>
                <span className="text-[11px] text-text-muted" title={formatDateTime(c.created_at)}>{timeAgo(c.created_at)}</span>
                {c.edited && <span className="text-[10px] text-text-muted italic" title={formatDateTime(c.updated_at)}>edited</span>}
                {c.can_edit && editingId !== c.id && (
                  <div className="ml-auto flex items-center gap-1">
                    <button onClick={() => { setEditingId(c.id); setEditDraft(c.body); }}
                      className="text-text-muted hover:text-primary p-1" title="Edit" data-testid={`comment-edit-${c.id}`}>
                      <PencilSimple size={12} weight="bold" />
                    </button>
                    <button onClick={() => remove(c.id)}
                      className="text-text-muted hover:text-danger p-1" title="Delete" data-testid={`comment-delete-${c.id}`}>
                      <Trash size={12} weight="bold" />
                    </button>
                  </div>
                )}
              </div>
              {editingId === c.id ? (
                <div className="p-3 space-y-2">
                  <textarea value={editDraft} onChange={e => setEditDraft(e.target.value)} rows={3} autoFocus
                    className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-primary"
                    data-testid={`comment-edit-input-${c.id}`} />
                  <div className="flex items-center gap-2 justify-end">
                    <button onClick={() => setEditingId(null)} className="px-3 py-1.5 text-xs text-text-secondary hover:text-text-primary">Cancel</button>
                    <button onClick={() => saveEdit(c.id)} disabled={!editDraft.trim()}
                      className="px-3 py-1.5 text-xs font-medium bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
                      data-testid={`comment-save-${c.id}`}>Save</button>
                  </div>
                </div>
              ) : (
                <div className="px-3 py-2.5 text-sm text-text-primary whitespace-pre-wrap break-words">{c.body}</div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="bg-surface border border-border rounded-lg p-3 space-y-2">
        <textarea
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') post(); }}
          rows={3}
          placeholder="Leave a comment… (⌘/Ctrl + Enter to post)"
          className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-primary"
          data-testid="comment-input"
        />
        <div className="flex justify-end">
          <button onClick={post} disabled={!draft.trim() || posting}
            className="px-4 py-1.5 text-xs font-medium bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid="comment-submit">
            <PaperPlaneTilt size={12} weight="bold" /> {posting ? 'Posting…' : 'Comment'}
          </button>
        </div>
      </div>
    </div>
  );
};

